import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import {
  CASHOUT_LIMITS_DEFAULT,
  CASHOUT_SCHEDULE_DEFAULT,
  Decimal,
  NO_METHOD_DEDUCTIONS,
  PAYOUT_DEDUCTION_KEYS,
  PAYOUT_DEDUCTION_LABELS,
  PAYOUT_EXPORT_DEFAULT,
  computePayoutDeductions,
  makePayoutMethod,
  type CashoutLimits,
  type CashoutSchedule,
  type FixedCharge,
  type PayoutDeductionKey,
  type PayoutMethod,
  type PayoutExportConfig,
  type PayoutMethodDeductions,
  type PayoutMethodField,
  type WtaxBracket,
} from '@mlm/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import { getTenantFeatures, patchTenantFeatures } from '../lib/api';
import { hasRole } from '../lib/auth-store';
import { ConfirmSaveButton } from '../components/confirm-save-button';

/**
 * Fees & Tax — typed editor for `tenant_config.features.payout_deductions`.
 * Payout methods are admin-managed data (add/rename/remove); each carries its
 * service charge, admin fee, and withholding-tax brackets. WTAX is flat-rate-
 * by-bracket, applied AFTER service + admin fees come off the gross. The
 * member's wallet is still debited the gross; these decide the net they
 * receive. super_admin only (the backend gates writes via `tenant.features.write`).
 */
function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

export function PayoutDeductionsPage() {
  const qc = useQueryClient();
  const canWrite = hasRole('super_admin');
  const q = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });

  const [methods, setMethods] = useState<PayoutMethod[]>([]);
  const [def, setDef] = useState<PayoutMethodDeductions>(NO_METHOD_DEDUCTIONS);
  // undefined = no custom order (the standard math). An array = cascading.
  const [order, setOrder] = useState<PayoutDeductionKey[] | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!q.data) return;
    const cfg = q.data.payout_deductions;
    setMethods(Array.isArray(cfg?.methods) ? clone(cfg.methods) : []);
    setDef(clone(cfg?.default ?? NO_METHOD_DEDUCTIONS));
    setOrder(Array.isArray(cfg?.order) && cfg.order.length > 0 ? clone(cfg.order) : undefined);
  }, [q.data]);

  // Omit `order` entirely when off, so the stored config stays exactly as it was
  // for every tenant that never turns this on.
  const nextConfig = order ? { methods, default: def, order } : { methods, default: def };

  const save = useMutation({
    mutationFn: () => patchTenantFeatures({ payout_deductions: nextConfig }),
    meta: { successMessage: 'Fees & tax saved.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-features'] }),
  });

  function updateMethod(i: number, patch: Partial<PayoutMethod>) {
    setMethods((ms) => ms.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  }
  function addMethod() {
    setMethods((ms) => [...ms, makePayoutMethod()]);
  }
  function removeMethod(i: number) {
    setMethods((ms) => ms.filter((_, idx) => idx !== i));
  }

  // Validate + surface the error inline; returns the message (or null) so the
  // confirm dialog only opens for valid input.
  function validate(): string | null {
    const codes = methods.map((m) => m.code.trim());
    let msg: string | null = null;
    if (codes.some((c) => c === '')) msg = 'Every method needs a code.';
    else if (codes.some((c) => !/^[a-z0-9_]+$/.test(c)))
      msg = 'Codes must be lowercase letters, digits, or underscores.';
    else if (new Set(codes).size !== codes.length) msg = 'Method codes must be unique.';
    else if (methods.some((m) => m.label.trim() === '')) msg = 'Every method needs a label.';
    setErr(msg);
    return msg;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Payout fees &amp; tax</h1>
        <p className="text-sm text-muted-foreground">
          Define the payout methods members can choose, each with its own deductions. By default
          service charge and admin fee come off the gross, then withholding tax (flat rate by
          bracket) applies to the remainder — set a custom <em>Deduction order</em> below to change
          that sequence. The member's wallet is debited the gross; these set the net they receive.
        </p>
        {!canWrite && (
          <p className="text-xs text-muted-foreground mt-1">
            Read-only. Payout methods &amp; fees are edited by a super-admin.
          </p>
        )}
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CashoutScheduleCard canWrite={canWrite} />
            <CashoutLimitsCard canWrite={canWrite} />
            <CashoutRequestRulesCard canWrite={canWrite} />
            <PayoutExportCard canWrite={canWrite} />
          </div>

          <DeductionOrderCard
            order={order}
            onChange={setOrder}
            canWrite={canWrite}
            sample={methods.find((m) => m.enabled)?.deductions ?? def}
          />

          {methods.length === 0 && (
            <Card>
              <CardContent className="py-6 text-sm text-muted-foreground">
                No payout methods yet. {canWrite ? 'Add one below.' : 'A super-admin can add them.'}{' '}
                Members can't request payouts until at least one method is enabled.
              </CardContent>
            </Card>
          )}

          {methods.map((m, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-start justify-between gap-3">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Display label">
                    <Input
                      className="h-8"
                      value={m.label}
                      disabled={!canWrite}
                      placeholder="Bank transfer"
                      onChange={(e) => updateMethod(i, { label: e.target.value })}
                    />
                  </Field>
                  <Field label="Code (stored on the request)">
                    <Input
                      className="h-8 font-mono"
                      value={m.code}
                      disabled={!canWrite}
                      placeholder="bank_transfer"
                      onChange={(e) =>
                        updateMethod(i, {
                          code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                        })
                      }
                    />
                  </Field>
                  <Field label="Account field label">
                    <Input
                      className="h-8"
                      value={m.accountLabel}
                      disabled={!canWrite}
                      placeholder="Bank account"
                      onChange={(e) => updateMethod(i, { accountLabel: e.target.value })}
                    />
                  </Field>
                  <Field label="Account field placeholder">
                    <Input
                      className="h-8"
                      value={m.accountPlaceholder ?? ''}
                      disabled={!canWrite}
                      placeholder="BPI · 0012345…"
                      onChange={(e) => updateMethod(i, { accountPlaceholder: e.target.value })}
                    />
                  </Field>
                  {/* Disbursement-file columns. Admin-typed rather than a
                      hardcoded bank list — every bank portal names its
                      processors differently. */}
                  <Field label="Bank / processor code (Proc)">
                    <Input
                      className="h-8"
                      value={m.procCode ?? ''}
                      disabled={!canWrite}
                      placeholder={m.code.toUpperCase() || 'BDO'}
                      onChange={(e) => updateMethod(i, { procCode: e.target.value.toUpperCase() })}
                    />
                  </Field>
                  <Field label="Default account type">
                    <Input
                      className="h-8"
                      value={m.defaultAccountType ?? ''}
                      disabled={!canWrite}
                      placeholder="Savings"
                      onChange={(e) => updateMethod(i, { defaultAccountType: e.target.value })}
                    />
                  </Field>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <label className="flex items-center gap-1.5 text-xs">
                    <Checkbox
                      checked={m.enabled}
                      disabled={!canWrite}
                      onCheckedChange={(v) => updateMethod(i, { enabled: v === true })}
                    />
                    Enabled
                  </label>
                  {canWrite && (
                    <button
                      type="button"
                      aria-label="Remove method"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => removeMethod(i)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                <FieldsEditor
                  fields={m.fields ?? []}
                  disabled={!canWrite}
                  fallbackLabel={m.accountLabel}
                  onChange={(fields) => updateMethod(i, { fields })}
                />
                <MethodEditor
                  value={m.deductions}
                  disabled={!canWrite}
                  cascading={!!order}
                  onChange={(deductions) => updateMethod(i, { deductions })}
                />
              </CardContent>
            </Card>
          ))}

          {canWrite && (
            <Button size="sm" variant="outline" onClick={addMethod}>
              <Plus className="h-3.5 w-3.5" /> Add payout method
            </Button>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Default (fallback for other methods)</CardTitle>
              <p className="text-xs text-muted-foreground">
                Applied if a payout arrives with a method not listed above. Usually left at zero.
              </p>
            </CardHeader>
            <CardContent>
              <MethodEditor
                value={def}
                disabled={!canWrite}
                cascading={!!order}
                onChange={setDef}
              />
            </CardContent>
          </Card>

          {canWrite && (
            <div className="flex items-center gap-2 pb-8">
              <ConfirmSaveButton
                label="Save fees & tax"
                title="Save fees & tax"
                current={q.data?.payout_deductions}
                next={nextConfig}
                loading={save.isPending}
                validate={validate}
                onInvalid={setErr}
                onConfirm={() => save.mutateAsync()}
              />
              {err && <span className="text-xs text-destructive">{err}</span>}
              {save.isSuccess && !err && <span className="text-xs text-success">Saved.</span>}
              {save.isError && <span className="text-xs text-destructive">Save failed.</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The global deduction order. OFF (no `order` stored) = the standard math both
 * fees off the gross, then WTAX on the remainder. ON = the listed sequence is
 * applied as a cascade, each step computed on what the previous ones left.
 *
 * Reordering is ↑/↓, not drag-and-drop: the repo carries no DnD library and
 * every other reorder in this admin (storefront rows, layout sections, stairstep
 * ranks) is ↑/↓, so a draggable list here would make this one screen the odd one
 * out — and arrows are keyboard-operable for free.
 *
 * The before/after preview is the point of the card. Turning cascading on can
 * genuinely move the net (with a percentage second fee it always does), and an
 * admin must see that here rather than discover it on a member's payout.
 */
const SAMPLE_GROSS = 10_000;

function DeductionOrderCard({
  order,
  onChange,
  canWrite,
  sample,
}: {
  order?: PayoutDeductionKey[];
  onChange: (v: PayoutDeductionKey[] | undefined) => void;
  canWrite: boolean;
  sample: PayoutMethodDeductions;
}) {
  const enabled = !!order;
  const list = order ?? [...PAYOUT_DEDUCTION_KEYS];

  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  }

  const gross = new Decimal(SAMPLE_GROSS);
  const before = computePayoutDeductions(gross, sample);
  const after = computePayoutDeductions(gross, sample, enabled ? list : null);
  const changed = !before.net.equals(after.net);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Deduction order</CardTitle>
        <p className="text-xs text-muted-foreground">
          Applies to every payout method (and to merchant cash-outs). Each method keeps its own
          rates — this only decides the sequence they come off in.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={enabled}
            disabled={!canWrite}
            onCheckedChange={(v) => onChange(v ? [...PAYOUT_DEDUCTION_KEYS] : undefined)}
          />
          <span>Use a custom order (each deduction comes off the running balance)</span>
        </label>

        {!enabled ? (
          <p className="text-xs text-muted-foreground">
            Standard: service charge and admin fee are both taken off the gross, then withholding
            tax is applied to what's left.
          </p>
        ) : (
          <div className="space-y-1.5">
            {list.map((key, i) => (
              <div key={key} className="flex items-center gap-2 rounded-md border px-3 py-2">
                <span className="w-5 text-xs tabular-nums text-muted-foreground">{i + 1}</span>
                <span className="flex-1 text-sm">{PAYOUT_DEDUCTION_LABELS[key]}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!canWrite || i === 0}
                  onClick={() => move(i, -1)}
                  aria-label={`Move ${PAYOUT_DEDUCTION_LABELS[key]} up`}
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!canWrite || i === list.length - 1}
                  onClick={() => move(i, 1)}
                  aria-label={`Move ${PAYOUT_DEDUCTION_LABELS[key]} down`}
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="rounded-md border bg-muted/40 p-3">
          <div className="text-xs font-medium">
            Preview on a ₱{SAMPLE_GROSS.toLocaleString()} payout
            <span className="ml-1 font-normal text-muted-foreground">
              (using the first enabled method’s rates, or the default profile)
            </span>
          </div>
          <table className="mt-2 w-full text-xs">
            <tbody>
              {after.steps.map((s, i) => (
                <tr key={`${s.key}-${i}`}>
                  <td className="py-0.5 text-muted-foreground">
                    {s.label}
                    {s.mode === 'percent' ? ` (${s.value.toString()}%)` : ''}
                  </td>
                  <td className="py-0.5 text-right tabular-nums">
                    −{s.amount.toFixed(2)}
                    <span className="ml-1 text-muted-foreground">of {s.base.toFixed(2)}</span>
                  </td>
                </tr>
              ))}
              <tr className="border-t">
                <td className="pt-1 font-medium">Net</td>
                <td className="pt-1 text-right font-medium tabular-nums">{after.net.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
          {changed && (
            <p className="mt-2 text-xs text-warning">
              This changes the net a member receives: {before.net.toFixed(2)} →{' '}
              {after.net.toFixed(2)} on a ₱{SAMPLE_GROSS.toLocaleString()} payout. Requests already
              issued keep the figures they were created with.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Per-method account inputs the member fills (e.g. bank: Bank name + Account
 * no. + Account name). Empty = the single `accountLabel` field is used. Each
 * field's `key` is where the member's value lands in `payout_requests.payload`.
 */
function FieldsEditor({
  fields,
  disabled,
  fallbackLabel,
  onChange,
}: {
  fields: PayoutMethodField[];
  disabled: boolean;
  fallbackLabel: string;
  onChange: (f: PayoutMethodField[]) => void;
}) {
  function setField(i: number, patch: Partial<PayoutMethodField>) {
    onChange(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  }
  function addField() {
    onChange([...fields, { key: '', label: '', placeholder: '', required: true }]);
  }
  function removeField(i: number) {
    onChange(fields.filter((_, idx) => idx !== i));
  }
  return (
    <div>
      <p className="text-sm font-medium">Account fields</p>
      <p className="text-xs text-muted-foreground">
        Inputs the member fills for this method. Leave empty to use the single “
        {fallbackLabel || 'Account details'}” field above.
      </p>
      {fields.length > 0 && (
        <table className="mt-2 text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="py-1 pr-3">Field label</th>
              <th className="py-1 pr-3">Key (stored in payload)</th>
              <th className="py-1 pr-3">Placeholder</th>
              <th className="py-1 pr-3">Role</th>
              <th className="py-1 pr-3">Options</th>
              <th className="py-1 pr-3">Required</th>
              <th className="py-1 pr-3">Show</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {fields.map((f, i) => (
              <tr key={i}>
                <td className="py-1 pr-3">
                  <Input
                    className="h-8 w-44"
                    value={f.label}
                    disabled={disabled}
                    placeholder="Bank name"
                    onChange={(e) => setField(i, { label: e.target.value })}
                  />
                </td>
                <td className="py-1 pr-3">
                  <Input
                    className="h-8 w-40 font-mono"
                    value={f.key}
                    disabled={disabled}
                    placeholder="bank_name"
                    onChange={(e) =>
                      setField(i, {
                        key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                      })
                    }
                  />
                </td>
                <td className="py-1 pr-3">
                  <Input
                    className="h-8 w-40"
                    value={f.placeholder ?? ''}
                    disabled={disabled}
                    placeholder="e.g. BPI"
                    onChange={(e) => setField(i, { placeholder: e.target.value })}
                  />
                </td>
                <td className="py-1 pr-3">
                  <select
                    className="h-8 w-40 rounded-md border bg-background px-2 text-sm disabled:opacity-50"
                    value={f.role ?? ''}
                    disabled={disabled}
                    onChange={(e) =>
                      setField(i, {
                        role: (e.target.value || undefined) as PayoutMethodField['role'],
                      })
                    }
                  >
                    <option value="">—</option>
                    <option value="account_holder">Account holder name</option>
                    <option value="account_number">Account number</option>
                    <option value="account_type">Account type (Savings/Current)</option>
                    <option value="email">Email address</option>
                    <option value="contact_number">Contact number</option>
                  </select>
                </td>
                <td className="py-1 pr-3">
                  <OptionsInput
                    options={f.options}
                    disabled={disabled}
                    onChange={(options) => setField(i, { options })}
                  />
                </td>
                <td className="py-1 pr-3 text-center">
                  <Checkbox
                    checked={f.required !== false}
                    disabled={disabled}
                    onCheckedChange={(v) => setField(i, { required: v === true })}
                  />
                </td>
                <td className="py-1 pr-3 text-center">
                  {/* Stored inverted as `hidden` — "Show" reads better in a table
                      of things that are almost always on. Unchecked = the member
                      never sees the field and the export falls back to the
                      method's default account type. */}
                  <Checkbox
                    checked={f.hidden !== true}
                    disabled={disabled}
                    onCheckedChange={(v) => setField(i, { hidden: v === true ? undefined : true })}
                  />
                </td>
                <td className="py-1">
                  {!disabled && (
                    <button
                      type="button"
                      aria-label="Remove field"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => removeField(i)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!disabled && (
        <Button size="sm" variant="outline" className="mt-2" onClick={addField}>
          <Plus className="h-3.5 w-3.5" /> Add field
        </Button>
      )}
      {fields.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          <strong>Role</strong> powers the cash-out security settings (Settings → KYC &amp;
          cash-out): tag the name field <em>Account holder name</em> for “Lock cash-out name to
          account owner”, and the account-number field <em>Account number</em> for “Unique cash-out
          account per member”. Tag <em>Email address</em> / <em>Contact number</em> to have those
          fields filled from the member&apos;s own profile — controlled by Settings → KYC &amp;
          cash-out “Autofill cash-out email &amp; contact number”. Leave <em>—</em> for fields those
          features don’t touch.{' '}
          <strong>Options</strong> (comma-separated, e.g. “Savings, Checking”) turns the field into
          a select the member picks from; empty keeps a free-text box. Untick <strong>Show</strong>{' '}
          to stop collecting the field — the bank export then uses the method’s default account
          type.
        </p>
      )}
    </div>
  );
}

/**
 * Comma-separated editor for a field's `options` list. Local draft state so a
 * trailing comma survives while typing (a parse-on-change controlled input
 * would normalize it away before the next option can be typed); the parsed
 * array propagates on every keystroke, the display text only re-syncs from
 * props when another row/method is loaded.
 */
function OptionsInput({
  options,
  disabled,
  onChange,
}: {
  options: string[] | undefined;
  disabled: boolean;
  onChange: (options: string[] | undefined) => void;
}) {
  const [draft, setDraft] = useState((options ?? []).join(', '));
  const [focused, setFocused] = useState(false);
  const fromProps = (options ?? []).join(', ');
  // Adopt external resets (method switch, discard) only while not editing.
  useEffect(() => {
    if (!focused) setDraft(fromProps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromProps]);
  return (
    <Input
      className="h-8 w-44"
      value={draft}
      disabled={disabled}
      placeholder="free text"
      title="Comma-separated choices; leave empty for a free-text box"
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        setDraft(fromProps);
      }}
      onChange={(e) => {
        setDraft(e.target.value);
        const opts = e.target.value
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        onChange(opts.length ? opts : undefined);
      }}
    />
  );
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Cash-out availability window (`tenant_config.features.cashout_schedule`).
 * Self-contained: its own query/state/save so it doesn't entangle the fees
 * editor's diff. The server rejects requests outside the window and the member
 * SPA disables submit.
 */
function CashoutScheduleCard({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const [sched, setSched] = useState<CashoutSchedule>(CASHOUT_SCHEDULE_DEFAULT);

  useEffect(() => {
    if (q.data?.cashout_schedule) setSched(clone(q.data.cashout_schedule));
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => patchTenantFeatures({ cashout_schedule: sched }),
    meta: { successMessage: 'Cash-out schedule saved.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-features'] }),
  });

  function toggleDay(d: number) {
    const set = new Set(sched.daysOfWeek);
    if (set.has(d)) set.delete(d);
    else set.add(d);
    setSched({ ...sched, daysOfWeek: [...set].sort((a, b) => a - b) });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Cash-out schedule</CardTitle>
        <p className="text-xs text-muted-foreground">
          When members may submit cash-out requests, in the tenant timezone. Disabled = always open.
          Requests outside the window are rejected.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={sched.enabled}
            disabled={!canWrite}
            onCheckedChange={(v) => setSched({ ...sched, enabled: v === true })}
          />
          Restrict cash-out to a schedule
        </label>
        {sched.enabled && (
          <>
            <div>
              <label className="text-xs text-muted-foreground">Open days</label>
              <div className="flex gap-1 mt-1 flex-wrap">
                {DAY_LABELS.map((lbl, d) => {
                  const on = sched.daysOfWeek.includes(d);
                  return (
                    <Button
                      key={d}
                      size="sm"
                      type="button"
                      variant={on ? 'default' : 'outline'}
                      disabled={!canWrite}
                      onClick={() => toggleDay(d)}
                    >
                      {lbl}
                    </Button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-end gap-3 flex-wrap">
              <Field label="Open hour (0–23)">
                <Input
                  className="h-8 w-24"
                  type="number"
                  min="0"
                  max="23"
                  value={String(sched.openHour)}
                  disabled={!canWrite}
                  onChange={(e) =>
                    setSched({
                      ...sched,
                      openHour: Math.min(24, Math.max(0, Number(e.target.value) || 0)),
                    })
                  }
                />
              </Field>
              <Field label="Close hour (1–24)">
                <Input
                  className="h-8 w-24"
                  type="number"
                  min="1"
                  max="24"
                  value={String(sched.closeHour)}
                  disabled={!canWrite}
                  onChange={(e) =>
                    setSched({
                      ...sched,
                      closeHour: Math.min(24, Math.max(0, Number(e.target.value) || 0)),
                    })
                  }
                />
              </Field>
              <span className="text-xs text-muted-foreground pb-2">
                Open {sched.openHour}:00 → {sched.closeHour}:00 local
              </span>
            </div>
          </>
        )}
        {canWrite && (
          <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save schedule'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Min/max cash-out bounds (`tenant_config.features.cashout_limits`). Per-slot
 * bounds a single request; per-member bounds the sum across all the member's
 * slots. Blank = no bound. Enforced in `POST /payouts/requests`.
 */
function CashoutLimitsCard({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const [limits, setLimits] = useState<CashoutLimits>(CASHOUT_LIMITS_DEFAULT);

  useEffect(() => {
    if (q.data?.cashout_limits) setLimits(clone(q.data.cashout_limits));
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => patchTenantFeatures({ cashout_limits: limits }),
    meta: { successMessage: 'Cash-out limits saved.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-features'] }),
  });

  // Empty input → null (unbounded); otherwise a non-negative number.
  const setBound = (key: keyof CashoutLimits, raw: string) => {
    const trimmed = raw.trim();
    const val = trimmed === '' ? null : Math.max(0, Number(trimmed) || 0);
    setLimits({ ...limits, [key]: val });
  };
  const boundStr = (v: number | null) => (v == null ? '' : String(v));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Cash-out limits</CardTitle>
        <p className="text-xs text-muted-foreground">
          Minimum and maximum cash-out amounts. Per-slot bounds a single request; per-member bounds
          the total across all of a member's slots. Leave blank for no limit.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-xs font-medium mb-1">Per slot (single request)</p>
          <div className="flex items-end gap-3 flex-wrap">
            <Field label="Minimum">
              <Input
                className="h-8 w-32"
                type="number"
                min="0"
                placeholder="No min"
                value={boundStr(limits.perSlotMin)}
                disabled={!canWrite}
                onChange={(e) => setBound('perSlotMin', e.target.value)}
              />
            </Field>
            <Field label="Maximum">
              <Input
                className="h-8 w-32"
                type="number"
                min="0"
                placeholder="No max"
                value={boundStr(limits.perSlotMax)}
                disabled={!canWrite}
                onChange={(e) => setBound('perSlotMax', e.target.value)}
              />
            </Field>
          </div>
        </div>
        <div>
          <p className="text-xs font-medium mb-1">Per member (across all slots)</p>
          <div className="flex items-end gap-3 flex-wrap">
            <Field label="Minimum">
              <Input
                className="h-8 w-32"
                type="number"
                min="0"
                placeholder="No min"
                value={boundStr(limits.perMemberMin)}
                disabled={!canWrite}
                onChange={(e) => setBound('perMemberMin', e.target.value)}
              />
            </Field>
            <Field label="Maximum">
              <Input
                className="h-8 w-32"
                type="number"
                min="0"
                placeholder="No max"
                value={boundStr(limits.perMemberMax)}
                disabled={!canWrite}
                onChange={(e) => setBound('perMemberMax', e.target.value)}
              />
            </Field>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Per-member max caps the sum of a member's outstanding requests plus the new one.
          </p>
        </div>
        {canWrite && (
          <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save limits'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Cash-out request rules: whether a member may stack multiple pending requests
 * (and the scope of "already pending"), and whether members may self-cancel a
 * not-yet-batched request. Both enforced in the payouts routes.
 */
function CashoutRequestRulesCard({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const [multipleAllowed, setMultipleAllowed] = useState(true);
  const [scope, setScope] = useState<'member' | 'slot'>('member');
  const [cancellationAllowed, setCancellationAllowed] = useState(true);

  useEffect(() => {
    if (!q.data) return;
    setMultipleAllowed(q.data.multiple_cashout_requests_allowed !== false);
    setScope(q.data.cashout_multiple_scope === 'slot' ? 'slot' : 'member');
    setCancellationAllowed(q.data.cashout_cancellation_allowed !== false);
  }, [q.data]);

  const save = useMutation({
    mutationFn: () =>
      patchTenantFeatures({
        multiple_cashout_requests_allowed: multipleAllowed,
        cashout_multiple_scope: scope,
        cashout_cancellation_allowed: cancellationAllowed,
      }),
    meta: { successMessage: 'Cash-out request rules saved.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-features'] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Cash-out request rules</CardTitle>
        <p className="text-xs text-muted-foreground">
          Control whether members can stack pending requests and whether they can cancel their own.
          Enforced server-side in the payout routes.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={multipleAllowed}
              disabled={!canWrite}
              onCheckedChange={(v) => setMultipleAllowed(v === true)}
            />
            Allow multiple pending cash-out requests
          </label>
          {!multipleAllowed && (
            <div className="ml-6 flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                Block when pending exists for the same
              </span>
              <Select
                value={scope}
                disabled={!canWrite}
                onValueChange={(v) => setScope(v === 'slot' ? 'slot' : 'member')}
              >
                <SelectTrigger className="h-8 w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">member</SelectItem>
                  <SelectItem value="slot">slot</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <p className="ml-6 text-[11px] text-muted-foreground">
            When off, a member with a request in requested/queued/processing can't create another.{' '}
            <b>member</b> = any of their slots; <b>slot</b> = only the same slot.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={cancellationAllowed}
            disabled={!canWrite}
            onCheckedChange={(v) => setCancellationAllowed(v === true)}
          />
          Allow members to cancel their own pending request
        </label>
        {canWrite && (
          <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Tenant constants stamped on every row of the payout batch export — the sheet
 * finance uploads to the bank. Everything else on that file comes off the
 * individual request; these are the columns that never vary.
 */
function PayoutExportCard({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const [cfg, setCfg] = useState<PayoutExportConfig>(PAYOUT_EXPORT_DEFAULT);

  useEffect(() => {
    if (!q.data?.payout_export) return;
    setCfg({ ...PAYOUT_EXPORT_DEFAULT, ...q.data.payout_export });
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => patchTenantFeatures({ payout_export: cfg }),
    meta: { successMessage: 'Export settings saved.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-features'] }),
  });

  const field = (key: keyof PayoutExportConfig, label: string, hint: string) => (
    <div>
      <label className="text-xs text-muted-foreground">{label}</label>
      <Input
        value={cfg[key]}
        disabled={!canWrite}
        onChange={(e) => setCfg((c) => ({ ...c, [key]: e.target.value }))}
      />
      <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Disbursement file</CardTitle>
        <p className="text-xs text-muted-foreground">
          Values stamped on every row of a payout batch export. The bank/e-wallet code per method (
          <b>Proc</b>) and the account type live on each payout method below.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {field(
          'txn_prefix',
          'Transaction reference prefix',
          "Prefixes each request's reference, e.g. PE → PE20260805000001. Changing it only affects NEW requests — existing references never change, or the bank would see a duplicate payment.",
        )}
        {field(
          'description',
          'Description',
          "The Description column. Leave blank to use each payout method's own label.",
        )}
        <div className="border-t pt-3 space-y-3">
          <p className="text-[11px] text-muted-foreground">
            Remittance-only columns — leave blank for straight bank transfers; some over-the-counter
            processors require them.
          </p>
          {field('sender_mobile', 'Sender mobile', "Your company's contact number.")}
          {field('sender_address', 'Sender address', "Your company's address.")}
          {field('purpose', 'Purpose', 'e.g. "Commission payout".')}
        </div>
        {canWrite && (
          <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function MethodEditor({
  value,
  disabled,
  cascading,
  onChange,
}: {
  value: PayoutMethodDeductions;
  disabled: boolean;
  /** A custom order is active, so percentages are of the RUNNING balance. */
  cascading?: boolean;
  onChange: (v: PayoutMethodDeductions) => void;
}) {
  return (
    <div className="space-y-5">
      <ChargeEditor
        label="Service charge"
        charge={value.service_charge}
        disabled={disabled}
        cascading={cascading}
        onChange={(service_charge) => onChange({ ...value, service_charge })}
      />
      <ChargeEditor
        label="Admin fee"
        charge={value.admin_fee}
        disabled={disabled}
        cascading={cascading}
        onChange={(admin_fee) => onChange({ ...value, admin_fee })}
      />
      <WtaxEditor
        wtax={value.withholding_tax}
        disabled={disabled}
        cascading={cascading}
        onChange={(withholding_tax) => onChange({ ...value, withholding_tax })}
      />
    </div>
  );
}

function ChargeEditor({
  label,
  charge,
  disabled,
  cascading,
  onChange,
}: {
  label: string;
  charge: FixedCharge;
  disabled: boolean;
  cascading?: boolean;
  onChange: (c: FixedCharge) => void;
}) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <label className="flex items-center gap-2 text-sm font-medium w-36">
        <Checkbox
          checked={charge.enabled}
          disabled={disabled}
          onCheckedChange={(v) => onChange({ ...charge, enabled: v === true })}
        />
        {label}
      </label>
      <Select
        value={charge.mode}
        disabled={disabled || !charge.enabled}
        onValueChange={(v) => onChange({ ...charge, mode: v as FixedCharge['mode'] })}
      >
        <SelectTrigger className="h-8 w-36">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {/* Under a custom order a percentage is of whatever the earlier steps
              left, not of the gross — the label must not keep saying "of gross". */}
          <SelectItem value="percent">
            {cascading ? '% of running balance' : '% of gross'}
          </SelectItem>
          <SelectItem value="flat">flat amount</SelectItem>
        </SelectContent>
      </Select>
      <Input
        className="h-8 w-28"
        type="number"
        step="0.01"
        min="0"
        value={String(charge.value)}
        disabled={disabled || !charge.enabled}
        onChange={(e) => onChange({ ...charge, value: Number(e.target.value) || 0 })}
      />
      <span className="text-xs text-muted-foreground">
        {charge.mode === 'percent' ? '%' : 'flat'}
      </span>
    </div>
  );
}

function WtaxEditor({
  wtax,
  disabled,
  cascading,
  onChange,
}: {
  wtax: { enabled: boolean; brackets: WtaxBracket[] };
  disabled: boolean;
  cascading?: boolean;
  onChange: (w: { enabled: boolean; brackets: WtaxBracket[] }) => void;
}) {
  const brackets = wtax.brackets;

  function setBracket(i: number, patch: Partial<WtaxBracket>) {
    onChange({ ...wtax, brackets: brackets.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) });
  }
  function addBracket() {
    onChange({ ...wtax, brackets: [...brackets, { upTo: null, percent: 0 }] });
  }
  function removeBracket(i: number) {
    onChange({ ...wtax, brackets: brackets.filter((_, idx) => idx !== i) });
  }

  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium">
        <Checkbox
          checked={wtax.enabled}
          disabled={disabled}
          onCheckedChange={(v) => onChange({ ...wtax, enabled: v === true })}
        />
        Withholding tax (flat rate by bracket)
      </label>
      {wtax.enabled && (
        <div className="mt-2 pl-6">
          <table className="text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-1 pr-3">Taxable up to (blank = and above)</th>
                <th className="py-1 pr-3">Rate %</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {brackets.map((b, i) => (
                <tr key={i}>
                  <td className="py-1 pr-3">
                    <Input
                      className="h-8 w-40"
                      type="number"
                      min="0"
                      placeholder="∞ (open-ended)"
                      value={b.upTo === null ? '' : String(b.upTo)}
                      disabled={disabled}
                      onChange={(e) =>
                        setBracket(i, {
                          upTo: e.target.value === '' ? null : Number(e.target.value) || 0,
                        })
                      }
                    />
                  </td>
                  <td className="py-1 pr-3">
                    <Input
                      className="h-8 w-24"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={String(b.percent)}
                      disabled={disabled}
                      onChange={(e) => setBracket(i, { percent: Number(e.target.value) || 0 })}
                    />
                  </td>
                  <td className="py-1">
                    {!disabled && (
                      <button
                        type="button"
                        aria-label="Remove bracket"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => removeBracket(i)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {brackets.length === 0 && (
            <p className="text-xs text-muted-foreground py-1">
              No brackets, WTAX is effectively 0. Add a bracket below.
            </p>
          )}
          {!disabled && (
            <Button size="sm" variant="outline" className="mt-2" onClick={addBracket}>
              <Plus className="h-3.5 w-3.5" /> Add bracket
            </Button>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            The whole taxable amount{' '}
            {cascading
              ? '(whatever the deductions placed above tax have left)'
              : '(gross − service − admin)'}{' '}
            is taxed at the rate of the first bracket its value falls into. Leave the last row's
            upper bound blank to cover all higher amounts.
          </p>
        </div>
      )}
    </div>
  );
}
